import { useState, useMemo, useEffect, useRef } from 'react';
import type { CategoryNode } from '@/api/materialCategory';

interface CategoryPermissionBlockProps {
  /** API 返回的完整分类树 */
  categoryTree: CategoryNode[];
  /** undefined = 全部可见（默认）；非空数组 = 仅限这些叶子节点 ID（格式: categoryId:level） */
  allowedModels?: string[] | undefined;
  onChange: (models: string[] | undefined) => void;
  compact?: boolean;
}

/** 叶子节点：id 为分类ID，level 为层级 */
interface LeafNode {
  id: string;
  level: number;
  label: string;
  icon: string;
  primaryColor: string;
}

export default function FolderPermissionBlock({
  categoryTree,
  allowedModels,
  onChange,
  compact = false,
}: CategoryPermissionBlockProps) {
  // 从分类树收集所有叶子节点
  const allLeaves = useMemo(() => {
    const leaves: LeafNode[] = [];
    for (const primary of categoryTree) {
      for (const sub of primary.subCategories) {
        if (sub.models.length > 0) {
          // 有三级分类 → 叶子是各个 model（三级）
          for (const model of sub.models) {
            leaves.push({ id: model.id, level: 3, label: model.label, icon: sub.icon, primaryColor: primary.color });
          }
        } else {
          // 无三级分类 → 叶子是二级分类本身
          leaves.push({ id: sub.id, level: 2, label: sub.label, icon: sub.icon, primaryColor: primary.color });
        }
      }
    }
    return leaves;
  }, [categoryTree]);

  // 叶子 ID 集合（用于快速查找）
  const allLeafIds = useMemo(() => allLeaves.map((l) => l.id), [allLeaves]);

  // 展开/折叠二级分类（默认折叠）
  const [expandedSubs, setExpandedSubs] = useState<Record<string, boolean>>({});

  // 内部模式状态："全部分类可见" vs "自定义分类"
  const [mode, setMode] = useState<'all' | 'custom'>(
    !allowedModels || allowedModels.length === 0 ? 'all' : 'custom'
  );

  // 仅在 allowedModels 空/非空状态转换时同步 mode，避免覆盖用户手动切换
  const prevAllowedEmpty = useRef(!allowedModels || allowedModels.length === 0);
  useEffect(() => {
    const currEmpty = !allowedModels || allowedModels.length === 0;
    if (prevAllowedEmpty.current !== currEmpty) {
      setMode(currEmpty ? 'all' : 'custom');
      prevAllowedEmpty.current = currEmpty;
    }
  }, [allowedModels]);

  const isAll = mode === 'all';

  // 已选中的叶子 ID 集合（从 categoryId:level 格式解析）
  const selectedLeafIds = useMemo(() => {
    if (isAll) return new Set(allLeafIds);
    return new Set((allowedModels ?? []).map((entry) => {
      const colonIdx = entry.lastIndexOf(':');
      return colonIdx >= 0 ? entry.substring(0, colonIdx) : entry;
    }));
  }, [isAll, allowedModels, allLeafIds]);

  // 获取所有叶子节点的完整格式列表
  const allLeafEntries = useMemo(
    () => allLeaves.map((l) => l.id + ':' + l.level),
    [allLeaves]
  );

  const toggleMode = () => {
    if (isAll) {
      // 切换到自定义：预选全部叶子
      setMode('custom');
      onChange(allLeafEntries);
    } else {
      // 切换回全部可见：传 undefined 表示清空自定义权限
      setMode('all');
      onChange(undefined);
    }
  };

  const toggleLeaf = (leaf: LeafNode) => {
    if (isAll) {
      // 从"全部分类可见"取消勾选某个叶子 → 切换到自定义模式
      const newEntries = allLeaves
        .filter((l) => l.id !== leaf.id)
        .map((l) => l.id + ':' + l.level);
      setMode('custom');
      onChange(newEntries.length === 0 ? [] : newEntries);
      return;
    }
    const current = allowedModels ?? [];
    const entry = leaf.id + ':' + leaf.level;
    const next = selectedLeafIds.has(leaf.id)
      ? current.filter((m) => !m.startsWith(leaf.id + ':'))
      : [...current, entry];
    onChange(next.length === 0 ? [] : next);
  };

  // 切换某个二级分类下所有叶子
  const toggleSubLeaves = (leaves: LeafNode[]) => {
    if (isAll) {
      // 从"全部分类可见"取消勾选整个二级分类 → 切换到自定义模式
      const leafIds = new Set(leaves.map((l) => l.id));
      const newEntries = allLeaves
        .filter((l) => !leafIds.has(l.id))
        .map((l) => l.id + ':' + l.level);
      setMode('custom');
      onChange(newEntries.length === 0 ? [] : newEntries);
      return;
    }
    const current = allowedModels ?? [];
    const allSelected = leaves.every((l) => selectedLeafIds.has(l.id));
    if (allSelected) {
      const removeIds = new Set(leaves.map((l) => l.id));
      onChange(current.filter((entry) => {
        const colonIdx = entry.lastIndexOf(':');
        const id = colonIdx >= 0 ? entry.substring(0, colonIdx) : entry;
        return !removeIds.has(id);
      }));
    } else {
      const addEntries = leaves.filter((l) => !selectedLeafIds.has(l.id)).map((l) => l.id + ':' + l.level);
      onChange([...current, ...addEntries]);
    }
  };

  // 切换某个一级分类下所有叶子
  const togglePrimaryLeaves = (leaves: LeafNode[]) => {
    toggleSubLeaves(leaves);
  };

  const selectAll = () => onChange(allLeaves.map((l) => l.id + ':' + l.level));
  const clearAll = () => onChange([]);

  const toggleSubExpand = (subId: string) => {
    setExpandedSubs((prev) => ({ ...prev, [subId]: !prev[subId] }));
  };

  const selectedCount = isAll ? allLeaves.length : selectedLeafIds.size;
  const totalCount = allLeaves.length;

  // 按一级分类分组
  const primaryGroups = useMemo(() => {
    const map = new Map<string, { primary: CategoryNode; leaves: LeafNode[] }>();
    for (const primary of categoryTree) {
      const leaves: LeafNode[] = [];
      for (const sub of primary.subCategories) {
        if (sub.models.length > 0) {
          for (const model of sub.models) {
            leaves.push({ id: model.id, level: 3, label: model.label, icon: sub.icon, primaryColor: primary.color });
          }
        } else {
          leaves.push({ id: sub.id, level: 2, label: sub.label, icon: sub.icon, primaryColor: primary.color });
        }
      }
      map.set(primary.id, { primary, leaves });
    }
    return map;
  }, [categoryTree, allLeaves]);

  return (
    <div>
      {/* 模式切换 */}
      <div className="flex items-center gap-3 mb-3">
        <button
          type="button"
          onClick={toggleMode}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
            isAll
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
          }`}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`${isAll ? 'ri-checkbox-circle-fill text-green-600' : 'ri-circle-line text-gray-400'} text-xs`}></i>
          </div>
          全部分类可见
        </button>
        <button
          type="button"
          onClick={toggleMode}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
            !isAll
              ? 'border-[#0069BD]/30 bg-[#0069BD]/5 text-[#0069BD]'
              : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
          }`}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i
              className={`${!isAll ? 'ri-checkbox-circle-fill' : 'ri-circle-line text-gray-400'} text-xs`}
              style={!isAll ? { color: '#0069BD' } : {}}
            ></i>
          </div>
          自定义分类
        </button>
        {!isAll && (
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs font-semibold cursor-pointer whitespace-nowrap"
              style={{ color: '#0069BD' }}
            >
              全选
            </button>
            <span className="text-gray-300 text-xs">|</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs font-semibold text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
            >
              清空
            </button>
          </div>
        )}
      </div>

      {/* 分类树（自定义模式下展示） */}
      {!isAll && (
        <div className="space-y-3">
          {categoryTree.map((primary) => {
            const group = primaryGroups.get(primary.id);
            const primaryLeaves = group?.leaves ?? [];
            const primarySelected = primaryLeaves.filter((l) => selectedLeafIds.has(l.id));
            const allPrimarySelected = primaryLeaves.length > 0 && primaryLeaves.every((l) => selectedLeafIds.has(l.id));
            const somePrimarySelected = primarySelected.length > 0 && !allPrimarySelected;

            return (
              <div key={primary.id} className="rounded-xl border border-gray-100 overflow-hidden">
                {/* 一级分类标题行 */}
                <button
                  type="button"
                  onClick={() => togglePrimaryLeaves(primaryLeaves)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-gray-50/80 hover:bg-gray-100/70 transition-colors cursor-pointer text-left"
                >
                  <div
                    className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: `${primary.color}18` }}
                  >
                    <i className={`${primary.icon} text-xs`} style={{ color: primary.color }}></i>
                  </div>
                  <span className="text-xs font-bold text-gray-700 flex-1">{primary.label}</span>
                  <span className="text-xs text-gray-400 mr-1">
                    {primarySelected.length}/{primaryLeaves.length}
                  </span>
                  {/* 一级复选框 */}
                  <div
                    className="w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all"
                    style={
                      allPrimarySelected
                        ? { background: primary.color, borderColor: primary.color }
                        : somePrimarySelected
                        ? { background: `${primary.color}30`, borderColor: primary.color }
                        : { background: 'white', borderColor: '#d1d5db' }
                    }
                  >
                    {allPrimarySelected && (
                      <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>
                    )}
                    {somePrimarySelected && (
                      <i className="ri-subtract-line" style={{ fontSize: '10px', color: primary.color }}></i>
                    )}
                  </div>
                </button>

                {/* 二级 + 叶子节点列表 */}
                <div className="divide-y divide-gray-50">
                  {primary.subCategories.map((sub) => {
                    const hasModels = sub.models.length > 0;
                    const isExpanded = expandedSubs[sub.id] ?? false;

                    if (hasModels) {
                      // 有三级分类：二级作为折叠组，叶子是各个 model
                      const subLeaves: LeafNode[] = sub.models.map((m) => ({
                        id: m.id, level: 3, label: m.label, icon: sub.icon, primaryColor: primary.color,
                      }));
                      const subSelected = subLeaves.filter((l) => selectedLeafIds.has(l.id));
                      const allSubSelected = subLeaves.length > 0 && subLeaves.every((l) => selectedLeafIds.has(l.id));
                      const someSubSelected = subSelected.length > 0 && !allSubSelected;

                      return (
                        <div key={sub.id} className="bg-white">
                          {/* 二级分类行 */}
                          <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50/60 transition-colors">
                            <button
                              type="button"
                              onClick={() => toggleSubExpand(sub.id)}
                              className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer shrink-0"
                            >
                              <i className={`${isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-sm`}></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleSubLeaves(subLeaves)}
                              className="flex items-center gap-2 flex-1 text-left cursor-pointer"
                            >
                              <div
                                className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                                style={{ background: `${primary.color}12` }}
                              >
                                <i className={`${sub.icon} text-xs`} style={{ color: primary.color }}></i>
                              </div>
                              <span className="text-xs font-semibold text-gray-600 flex-1">{sub.label}</span>
                              <span className="text-xs text-gray-400 mr-1">
                                {subSelected.length}/{subLeaves.length}
                              </span>
                            </button>
                            {/* 二级复选框 */}
                            <button
                              type="button"
                              onClick={() => toggleSubLeaves(subLeaves)}
                              className="shrink-0 cursor-pointer"
                            >
                              <div
                                className="w-4 h-4 rounded border flex items-center justify-center transition-all"
                                style={
                                  allSubSelected
                                    ? { background: primary.color, borderColor: primary.color }
                                    : someSubSelected
                                    ? { background: `${primary.color}30`, borderColor: primary.color }
                                    : { background: 'white', borderColor: '#d1d5db' }
                                }
                              >
                                {allSubSelected && (
                                  <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>
                                )}
                                {someSubSelected && (
                                  <i className="ri-subtract-line" style={{ fontSize: '10px', color: primary.color }}></i>
                                )}
                              </div>
                            </button>
                          </div>

                          {/* 三级叶子列表 */}
                          {isExpanded && (
                            <div className={`grid gap-1.5 px-3 pb-2.5 pt-1 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'} pl-9`}>
                              {sub.models.map((model) => {
                                const selected = selectedLeafIds.has(model.id);
                                return (
                                  <button
                                    key={model.id}
                                    type="button"
                                    onClick={() => toggleLeaf({ id: model.id, level: 3, label: model.label, icon: sub.icon, primaryColor: primary.color })}
                                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-left transition-all cursor-pointer ${
                                      selected ? '' : 'border-gray-100 bg-gray-50/50 hover:bg-gray-100/60'
                                    }`}
                                    style={
                                      selected
                                        ? { borderColor: `${primary.color}40`, background: `${primary.color}08` }
                                        : {}
                                    }
                                  >
                                    <div
                                      className="w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-all"
                                      style={
                                        selected
                                          ? { background: primary.color, borderColor: primary.color }
                                          : { background: 'white', borderColor: '#d1d5db' }
                                      }
                                    >
                                      {selected && (
                                        <i className="ri-check-line text-white" style={{ fontSize: '9px' }}></i>
                                      )}
                                    </div>
                                    <span
                                      className="text-xs font-medium truncate flex-1"
                                      style={{ color: selected ? primary.color : '#6b7280' }}
                                    >
                                      {model.label}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      // 无三级分类：二级本身就是叶子节点
                      const leaf: LeafNode = { id: sub.id, level: 2, label: sub.label, icon: sub.icon, primaryColor: primary.color };
                      const selected = selectedLeafIds.has(sub.id);

                      return (
                        <div key={sub.id} className="bg-white">
                          <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50/60 transition-colors">
                            <div className="w-4 shrink-0"></div>
                            <div className="flex items-center gap-2 flex-1">
                              <div
                                className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                                style={{ background: `${primary.color}12` }}
                              >
                                <i className={`${sub.icon} text-xs`} style={{ color: primary.color }}></i>
                              </div>
                              <span className="text-xs font-semibold text-gray-600 flex-1">{sub.label}</span>
                              <span className="text-xs text-gray-300 italic">无型号</span>
                            </div>
                            {/* 二级叶子复选框 */}
                            <button
                              type="button"
                              onClick={() => toggleLeaf(leaf)}
                              className="shrink-0 cursor-pointer"
                            >
                              <div
                                className="w-4 h-4 rounded border flex items-center justify-center transition-all"
                                style={
                                  selected
                                    ? { background: primary.color, borderColor: primary.color }
                                    : { background: 'white', borderColor: '#d1d5db' }
                                }
                              >
                                {selected && (
                                  <i className="ri-check-line text-white" style={{ fontSize: '10px' }}></i>
                                )}
                              </div>
                            </button>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 摘要说明 */}
      <p className="text-xs text-gray-400 mt-2">
        {isAll
          ? `该代理商可访问全部 ${totalCount} 个分类的资料。`
          : `已选 ${selectedCount} / ${totalCount} 个分类，代理商仅可访问已勾选分类的资料。`}
      </p>
    </div>
  );
}
